import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, OnDestroy, ViewChild } from '@angular/core';
import { ResizableModule, ResizeEvent } from 'angular-resizable-element';
import { getEnumValues } from '../../core/utilities';
import { Store } from '@ngrx/store';
import { combineLatestWith, Observable } from 'rxjs';
import { LocationsSkeletonService } from './locations-skeleton.service';
import { LocationsSkeletonState } from './state/locations-skeleton-state';
import * as skeletonSelectors from './state/locations-skeleton-selectors';
import { SkeletonBottomPanelActions, SkeletonGeneralActions, SkeletonRightPanelActions } from './state/locations-skeleton-actions';
import { LocationsLifecycleActions } from './location-list/state/location-list-actions';
import { LocationListApiService } from './location-list/location-list-api.service';
import { LocationListService } from './location-list/location-list.service';
import { LocationListLocalStoreService } from './location-list/location-list-local-store.service';
import { EditLocationComponent } from './edit-location/edit-location.component';
import { DefaultMapComponent } from './map/default-map/default-map.component';
import { BlsUpdatesComponent } from './bls-updates/bls-updates.component';
import { BlsGmbDifferencesComponent } from './bls-gmb-differences/bls-gmb-differences.component';
import { CCListComponent } from './cc-list/cc-list.component';
import { MatIcon } from '@angular/material/icon';
import { NgClass } from '@angular/common';
import { MatIconButton } from '@angular/material/button';
import { LocationListComponent } from './location-list/location-list.component';
import { LetDirective } from '@ngrx/component';
import { CcListApiService } from './cc-list/cc-list-api.service';
import { CcListLocalStoreService } from './cc-list/cc-list-local-store.service';
import { CvMapComponent } from './map/cv-map/cv-map.component';
import { selectIsCvOn } from './state/location-skeleton-combined-selectors';
import { selectGoogleMapsApiLoaded } from '../../store/global/core-selectors';
import { map } from 'rxjs/operators';
import { ValidationHistoryComponent } from './validation-history/validation-history.component';

export interface ResizeBottomPanelPayload {
  bottomPanelHeight: number;
  totalHeight: number;
}

interface LocationsSkeletonViewModel extends LocationsSkeletonState {
  cvOn: boolean,
  googleMapsApiLoaded: boolean
}

export enum RightPanelTabs {
  MAP = 'Map',
  EDIT = 'Edit'
}

export enum BottomPanelTabs {
  CC_RESULTS = 'CC results',
  COMPARE = 'Compare',
  UPDATES = 'Updates',
  VALIDATION_HISTORY = 'Validation history'
}

@Component({
  selector: 'app-locations',
  templateUrl: './locations-skeleton.component.html',
  styleUrls: ['./locations-skeleton.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    // needs to be at this level so all children have access
    LocationListApiService,
    LocationListService,
    CcListApiService,
    CcListLocalStoreService,
    // TODO: It would be better though to provide this at location list component level and create a
    //  skeleton local store -> move shared state there
    // TODO: also refactor existing global store and move UI state to this newly created local store
    LocationListLocalStoreService,
  ],
  standalone: true,
  imports: [
    LetDirective,
    LocationListComponent,
    ResizableModule,
    NgClass,
    MatIcon,
    MatIconButton,
    CCListComponent,
    BlsGmbDifferencesComponent,
    BlsUpdatesComponent,
    DefaultMapComponent,
    EditLocationComponent,
    CvMapComponent,
    ValidationHistoryComponent,
  ],
})

export class LocationsSkeletonComponent implements AfterViewInit, OnDestroy {

  @ViewChild('mainPanel') mainPanel: ElementRef;
  @ViewChild('container') container: ElementRef;

  viewModel$: Observable<LocationsSkeletonViewModel> = this.store.select(skeletonSelectors.state).pipe(
      combineLatestWith(this.store.select(selectIsCvOn), this.store.select(selectGoogleMapsApiLoaded)),
      map(([skeletonLayout, cvOn, googleMapsApiLoaded]) => ({
        ...skeletonLayout,
        cvOn,
        googleMapsApiLoaded,
      })),
  );


  rightPanelTabs = RightPanelTabs;
  bottomPanelTabs = BottomPanelTabs;
  getEnumValues = getEnumValues;
  getSvgName = this.skeletonService.getSvgName;

  constructor(private store: Store,
              private skeletonService: LocationsSkeletonService) {
  }

  ngAfterViewInit() { // safe to access HTML elements here
    this.store.dispatch(SkeletonGeneralActions
        .initializeContainerDimensions(
            {
              totalHeight: this.mainPanel.nativeElement.offsetHeight,
              totalWidth: this.container.nativeElement.offsetWidth,
            }));
  }

  ngOnDestroy() {
    this.store.dispatch(LocationsLifecycleActions.locationsComponentDestroyed());
  }

  /**
   * Attempt to resize top panel
   * @param e
   */
  onResizeBottomPanel(e: ResizeEvent) {
    const payload: ResizeBottomPanelPayload = {
      bottomPanelHeight: e.rectangle.height,
      totalHeight: this.mainPanel.nativeElement.offsetHeight,
    };
    this.store.dispatch(SkeletonBottomPanelActions.resizeBottomPanel({ payload: payload }));
  }

  /**
   * Attempt to resize right panel
   * @param e
   */
  onResizeRightPanel(e: ResizeEvent) {
    this.store.dispatch(SkeletonRightPanelActions.resizeRightPanel({
      totalWidth: this.container.nativeElement.offsetWidth,
      rightPanelWidth: e.rectangle.width,
    }));
  }

  /**
   * Dispatching the below action toggle 'resizing' bool flag. change detection of child components
   * should be off if 'resizing' = true
   */
  onResizeToggle() {
    this.store.dispatch(SkeletonGeneralActions.toggleResizing());
  }

  /**
   * Update selection in store
   * @param tab
   */
  onBottomTabClick(tab: BottomPanelTabs) {
    this.store.dispatch(SkeletonBottomPanelActions.userSelectBottomPanelTab({ payload: tab }));
  }

  /**
   * Update selection in store
   * @param tab
   */
  onRightTabClick(tab: RightPanelTabs) {
    this.store.dispatch(SkeletonRightPanelActions.userSelectRightPanelTab({ payload: tab }));
  }

  /**
   * Toggle bottom panel and update state
   */
  onToggleBottomPanelClick() {
    this.store.dispatch(SkeletonBottomPanelActions.userToggleBottomPanel({}));
  }

  /**
   * Toggle right panel and update state
   */
  onToggleRightPanelClick() {
    this.store.dispatch(SkeletonRightPanelActions.userToggleRightPanel({}));
  }

}
